import {
  completedFiles,
  displayOrDash,
  recordRoomStatusPillClass,
  type CompletedFileRow,
} from '../data/recordRoom';
import type { UpdateStoredLocationPayload } from '../components/recordRoom/UpdateStoredLocationModal';
import { dateRangeColumnFilterToBounds, uniqueColumnSuggestions } from '../utils/columnFilter';
import { formatDisplayDateTime } from '../utils/dateFormat';
import {
  caseApiRequest,
  dataPayloadErrorMessage,
  isDataPayloadSuccess,
} from './apiClient';

export type PhysicalFileListResult = {
  items: CompletedFileRow[];
  total: number;
  totalPages: number;
};

export type PhysicalFileListParams = {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'ASC' | 'DESC';
  columnFilters?: Record<string, string>;
  signal?: AbortSignal;
};

export type UpdateStoredLocationResult = { success: true } | { success: false; message: string };

type ApiPhysicalFileRow = {
  case_request_id?: string;
  reference_no?: string;
  category_name?: string;
  sub_category_name?: string;
  submitted_agency?: string;
  case_completed_date?: string;
  physical_file_status_name?: string;
  physical_file_status_code?: string;
  file_movement_date_time?: string;
  current_custody?: string;
  storage_location?: string;
};

type ApiPhysicalFilePage = {
  content?: ApiPhysicalFileRow[];
  totalElements?: number;
  totalPages?: number;
};

const FILTER_COL_MAP: Record<string, string> = {
  ref: 'reference_no',
  fileCategory: 'category_name',
  subFileCategory: 'sub_category_name',
  agency: 'submitted_agency',
  fs: 'physical_file_status_name',
  custody: 'current_custody',
  storageLocation: 'storage_location',
};

const SUGGESTION_VALUE: Record<string, (row: CompletedFileRow) => string> = {
  ref: (r) => r.ref,
  fileCategory: (r) => r.fileCategory,
  subFileCategory: (r) => r.subFileCategory,
  agency: (r) => r.agency,
  fs: (r) => r.statusLabel,
  custody: (r) => r.custody,
  storageLocation: (r) => r.storageLocation,
};

export const SORT_COL_MAP: Record<string, string> = {
  ref: 'REFERENCE_NO',
  fileCategory: 'CATEGORY_NAME',
  subFileCategory: 'SUB_CATEGORY_NAME',
  agency: 'SUBMITTED_AGENCY',
  completedAt: 'CASE_COMPLETED_DATE',
  fs: 'PHYSICAL_FILE_STATUS',
  movementAt: 'FILE_MOVEMENT_DATE_TIME',
  custody: 'CURRENT_CUSTODY',
  storageLocation: 'STORAGE_LOCATION',
};

function strField(row: ApiPhysicalFileRow, ...keys: (keyof ApiPhysicalFileRow)[]): string {
  for (const key of keys) {
    const v = row[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function adaptPhysicalFileRow(row: ApiPhysicalFileRow): CompletedFileRow {
  const ref = strField(row, 'reference_no');
  const statusName = strField(row, 'physical_file_status_name');
  const statusCode = strField(row, 'physical_file_status_code');
  const completedRaw = strField(row, 'case_completed_date');
  const movementRaw = strField(row, 'file_movement_date_time');

  const completedAt = completedRaw
    ? displayOrDash(formatDisplayDateTime(completedRaw) || completedRaw)
    : displayOrDash(null);
  const movementAt = movementRaw
    ? displayOrDash(formatDisplayDateTime(movementRaw) || movementRaw)
    : displayOrDash(null);

  return {
    ref: displayOrDash(ref),
    fileCategory: displayOrDash(strField(row, 'category_name')),
    subFileCategory: displayOrDash(strField(row, 'sub_category_name')),
    agency: displayOrDash(strField(row, 'submitted_agency')),
    completedAt,
    statusLabel: displayOrDash(statusName),
    statusCode,
    statusClass: recordRoomStatusPillClass(statusCode, statusName),
    movementAt,
    custody: displayOrDash(strField(row, 'current_custody')),
    storageLocation: displayOrDash(strField(row, 'storage_location')),
    caseRequestId: strField(row, 'case_request_id'),
  };
}

/** CMS-CRC-15 — POST /physical-file/list request body. */
export function buildPhysicalFileListBody(params: PhysicalFileListParams): Record<string, unknown> {
  const body: Record<string, unknown> = {
    sort_by: params.sortBy ?? 'REFERENCE_NO',
    sort_direction: params.sortDir ?? 'ASC',
  };

  if (params.columnFilters) {
    for (const [colId, value] of Object.entries(params.columnFilters)) {
      const trimmed = value.trim();
      if (!trimmed) continue;
      if (colId === 'completedAt') {
        const range = dateRangeColumnFilterToBounds(trimmed);
        if (range.from) body.case_completed_at_from = range.from;
        if (range.to) body.case_completed_at_to = range.to;
        continue;
      }
      if (colId === 'movementAt') {
        const range = dateRangeColumnFilterToBounds(trimmed);
        if (range.from) body.file_movement_date_time_from = range.from;
        if (range.to) body.file_movement_date_time_to = range.to;
        continue;
      }
      const apiField = FILTER_COL_MAP[colId];
      if (apiField) body[apiField] = trimmed;
    }
  }

  return body;
}

function mockFallback(): PhysicalFileListResult {
  return {
    items: completedFiles,
    total: completedFiles.length,
    totalPages: 1,
  };
}

/** Column search auto-suggestions (min 3 characters) via list API. */
export async function fetchPhysicalFileColumnSuggestions(
  colId: string,
  query: string,
  signal?: AbortSignal,
): Promise<string[]> {
  const extract = SUGGESTION_VALUE[colId];
  if (!extract || !FILTER_COL_MAP[colId]) return [];

  const result = await listPhysicalFiles({
    page: 0,
    pageSize: 20,
    columnFilters: { [colId]: query.trim() },
    signal,
  });

  return uniqueColumnSuggestions(result.items.map(extract), query);
}

/** CMS-CRC-15 — paginated completed physical file records. */
export async function listPhysicalFiles(
  params: PhysicalFileListParams = {},
): Promise<PhysicalFileListResult> {
  const page = params.page ?? 0;
  const pageSize = params.pageSize ?? 10;
  const body = buildPhysicalFileListBody(params);

  const res = await caseApiRequest<ApiPhysicalFilePage>(
    `/physical-file/list?page=${page}&size=${pageSize}`,
    { method: 'POST', body, signal: params.signal },
  );

  if (!res.ok) {
    if (import.meta.env.VITE_USE_MOCK_API === 'true') return mockFallback();
    return { items: [], total: 0, totalPages: 1 };
  }

  const content = Array.isArray(res.data?.content) ? res.data.content : [];
  return {
    items: content.map(adaptPhysicalFileRow),
    total: res.data?.totalElements ?? content.length,
    totalPages: Math.max(1, res.data?.totalPages ?? 1),
  };
}

/** CMS-CRC-16 — update physical file stored location. */
export async function updateStoredLocation(
  payload: UpdateStoredLocationPayload,
): Promise<UpdateStoredLocationResult> {
  const caseRequestId = payload.caseRequestId.trim();
  if (!caseRequestId) {
    return { success: false, message: 'Case request id is required.' };
  }

  const res = await caseApiRequest<{ status?: string; message?: string }>(
    `/physical-file/${encodeURIComponent(caseRequestId)}/location`,
    {
      method: 'PUT',
      body: {
        file_ref_no: payload.fileRef.trim(),
        file_location: payload.location.trim(),
      },
    },
  );

  if (!res.ok) {
    return { success: false, message: res.message || 'Could not update stored location.' };
  }

  if (!isDataPayloadSuccess(res.data)) {
    return { success: false, message: dataPayloadErrorMessage(res.data) };
  }

  return { success: true };
}
